import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import SearchBoxDropdown from '@adsk/bim360-matrix-react-search-box-dropdown';
import InfoPopover from '@adsk/bim360-matrix-react-info-popover';
import Text from '@adsk/bim360-matrix-react-text';
import alertWarning from '../../assets/images/icons/alert-warning-fill-red.svg';
import i18n from '../../utils/i18n';
import ProjectViewExport from './ProjectViewExport';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {createSearchQuery, stripSearchQuery} from '../../utils/query-helpers';
import {ISSUE_FILTER_PROPERTIES, PAGE_PARAMS} from '../../constants/issue-query-params';
import featureFlags, {FEATURE_FLAGS} from '../../utils/featureFlags';
import {preloadedFilterQueryParamsState} from '../../constants/preloaded-states';
import {stopPropagation} from '../../utils/dom-helpers';
import FilterIcon from '../common/FilterIcon';
import FilterIndicator from '../common/FilterIndicator';
import {reportFormats} from '../../constants/analytics';
import {
  changeLocationAction,
  clearSearchResultsAction,
  createBcfFileAction,
  csvLimitExceeded,
  fetchIssuesReport,
  getSearchResultsAction,
  setQueryParamsAction
} from '../../app/actions';
import {
  getCurrentIssuesCount,
  getCurrentViewType,
  getIssuesCollectionLoading,
  getIssuesCollectionSearchResults,
  getQueryParams,
  isFetchReportLoading as getIsFetchReportLoading,
  isFiltered as getIsFiltered,
  isFilteredByCustomAttributes as getIsFilteredByCustomAttributes
} from '../../app/selectors';
import {connect} from '../../app/connect';
import {CSV_ISSUE_LIMIT} from '../../constants/csv-report';
import {isFedrampRegion} from '../../utils/region-helpers';

class ProjectGridHeader extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      searchExpression: stripSearchQuery(props.queryParams[ISSUE_FILTER_PROPERTIES.SEARCH])
    };

    this.searchBoxDropdownInputProps = {
      'data-testid': 'ProjectGridHeader__search'
    };

    this.openFilterPanelIfClosed = this.openFilterPanelIfClosed.bind(this);
    this.resetFilters = this.resetFilters.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.queryParams[ISSUE_FILTER_PROPERTIES.SEARCH] !== this.props.queryParams[ISSUE_FILTER_PROPERTIES.SEARCH]
    ) {
      const nextSearchExpression = stripSearchQuery(nextProps.queryParams[ISSUE_FILTER_PROPERTIES.SEARCH]);
      if (nextSearchExpression !== this.state.searchExpression) {
        this.setState({searchExpression: nextSearchExpression});
      }
    }
  }

  getPreloadedQueryParamsState() {
    return preloadedFilterQueryParamsState[this.props.viewType][TYPE_QUALITY_ISSUES];
  }

  onSearch(searchExpression) {
    const searchQuery = searchExpression ? createSearchQuery(searchExpression) : '';
    this.setState({searchExpression});
    this.props.dispatch(
      setQueryParamsAction(
        {
          [ISSUE_FILTER_PROPERTIES.SEARCH]: searchQuery.trim(),
          [PAGE_PARAMS.OFFSET]: 0
        },
        TYPE_QUALITY_ISSUES
      )
    );
  }

  handleSuggestedClick(attributes) {
    const {dispatch} = this.props;
    dispatch(changeLocationAction(`/project/${TYPE_QUALITY_ISSUES}/${attributes.id}`));
  }

  getSearchSuggestedSearchResults(searchExpression) {
    this.setState({searchExpression});
    if (searchExpression) {
      const searchQuery = createSearchQuery(searchExpression);
      this.props.dispatch(getSearchResultsAction(searchQuery));
    } else {
      this.props.dispatch(clearSearchResultsAction());
    }
  }

  openFilterPanelIfClosed() {
    !this.props.isFilterPanelOpen && this.props.toggleFilterPanel();
  }

  resetFilters(event) {
    stopPropagation(event);
    this.props.dispatch(setQueryParamsAction(this.getPreloadedQueryParamsState()));
  }

  render() {
    const {
      createBtn,
      isFetchReportLoading,
      loading,
      canExportPDF,
      canExportXLSX,
      dispatch,
      showExportModal,
      isFilterPanelOpen,
      isFiltered,
      disableExportBtn,
      currentIssueCount,
      container
    } = this.props;

    const searchResults = this.props.searchResults || [];
    const disableExportForNoIssues = currentIssueCount === 0 ? true : false;
    const canExportBCF =
      featureFlags.get(FEATURE_FLAGS.BCF_EXPORT_ENABLED) &&
      !featureFlags.get(FEATURE_FLAGS.BIM360_BCF_REPORT) &&
      !isFedrampRegion();

    const exportOptions = [];

    if (!isFedrampRegion() && !featureFlags.get(FEATURE_FLAGS.REMOVE_CSV_EXPORT)) {
      const exportCSVLabel =
        canExportBCF || canExportPDF || canExportXLSX ? i18n.t('export.csvReport') : i18n.t('export.export');

      exportOptions.push({
        eventKey: 2,
        label: exportCSVLabel,
        handleOnClick: () => {
          if (currentIssueCount > CSV_ISSUE_LIMIT) {
            dispatch(csvLimitExceeded());
            return;
          }
          dispatch(fetchIssuesReport());
        },
        icon: (
          <InfoPopover
            container={container}
            message={<Text hasHTML>{i18n.t('export.csvDecommissioned')}</Text>}
            icon={alertWarning}
            iconWidth="20px"
            iconHeight="20px"
          />
        )
      });
    }

    if (canExportXLSX) {
      exportOptions.unshift({
        eventKey: 4,
        label: i18n.t('export.xlsxReport'),
        handleOnClick: () => showExportModal(reportFormats.XLSX)
      });
    }

    if (canExportPDF) {
      exportOptions.unshift({
        eventKey: 1,
        label: i18n.t('export.pdfReport'),
        handleOnClick: () => showExportModal(reportFormats.PDF)
      });
    }

    if (canExportBCF) {
      exportOptions.push({
        eventKey: 3,
        label: i18n.t('export.exportBCF'),
        handleOnClick: () => dispatch(createBcfFileAction())
      });
    }

    return (
      <div className="ProjectGridHeader" data-testid="ProjectGridHeader">
        <div className="ProjectGridHeader__left-buttons">
          <FilterIcon
            isFilterOpen={isFilterPanelOpen}
            toggleFilterPanel={this.props.toggleFilterPanel}
            dataTestId="ProjectGridHeader__filter-button"
          />
          <SearchBoxDropdown
            className="ProjectGridHeader__search-box-container"
            placeholder={i18n.t('search')}
            onSearch={this.onSearch.bind(this)}
            searchExpression={this.state.searchExpression}
            searchForSuggestedResults={this.getSearchSuggestedSearchResults.bind(this)}
            onSuggestedResultRowSelect={this.handleSuggestedClick.bind(this)}
            suggestionList={searchResults}
            inputProps={this.searchBoxDropdownInputProps}
          />
          {isFiltered && <FilterIndicator onClick={this.openFilterPanelIfClosed} resetFilters={this.resetFilters} />}
        </div>
        <div className="ProjectGridHeader__right-buttons">
          <ProjectViewExport
            exportOptions={exportOptions}
            disabled={isFetchReportLoading || loading || disableExportBtn || disableExportForNoIssues}
            disabledReason={disableExportBtn && i18n.t('export.exportDisabledByCustomFilters')}
            exportingText={i18n.t('export.exporting')}
            isFetchReportLoading={isFetchReportLoading}
          />
          {createBtn}
        </div>
      </div>
    );
  }
}

ProjectGridHeader.displayName = 'ProjectGridHeader';

ProjectGridHeader.propTypes = {
  queryParams: PropTypes.object,
  createBtn: PropTypes.node,
  isFetchReportLoading: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  canExportPDF: PropTypes.bool.isRequired,
  canExportXLSX: PropTypes.bool.isRequired,
  showExportModal: PropTypes.func.isRequired,
  searchResults: PropTypes.array,
  toggleFilterPanel: PropTypes.func.isRequired,
  isFilterPanelOpen: PropTypes.bool,
  isFiltered: PropTypes.bool,
  viewType: PropTypes.string.isRequired,
  container: PropTypes.object,
  disableExportBtn: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  currentIssueCount: PropTypes.number
};

const mapStateToProps = state => ({
  viewType: getCurrentViewType(state),
  queryParams: getQueryParams(state),
  loading: getIssuesCollectionLoading(state),
  canExportPDF: true,
  canExportXLSX: true,
  isFetchReportLoading: getIsFetchReportLoading(state),
  searchResults: getIssuesCollectionSearchResults(state),
  isFiltered: getIsFiltered(state),
  disableExportBtn:
    getIsFilteredByCustomAttributes(state) && !featureFlags.get(FEATURE_FLAGS.EXPORT_WITH_CUSTOM_ATTRIBUTES_FILTERS),
  currentIssueCount: getCurrentIssuesCount(state)
});

export default connect(mapStateToProps)(ProjectGridHeader);
